import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { useAuth } from '../../context/AuthContext';
import { favoritesApi } from '../../services/apiService';
import { shouldShowVerifiedBadge } from '../../utils/membershipUtils';
import { API_BASE_URL } from '../../config/api';
import Pagination from '../../components/Pagination/Pagination';
import './TopSellerPage.css';

const TopSellerPage = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const { user, isAuthenticated } = useAuth();

  // Fetch sellers from API
  useEffect(() => {
    const fetchSellers = async () => {
      try {
        console.log('Fetching sellers from:', `${API_BASE_URL}/sellers`);
        const response = await fetch(`${API_BASE_URL}/sellers`);
        const data = await response.json();
        console.log('Sellers API response:', data);
        // Handle both array response and object with sellers property
        const sellersArray = Array.isArray(data) ? data : (data.sellers || []);
        console.log('Sellers to display:', sellersArray.length);
        setSellers(sellersArray);
      } catch (error) {
        console.error('Error fetching sellers:', error);
        // Fallback to sample data
        setSellers(Array(20).fill(null).map((_, index) => ({
          id: index + 1,
          name: 'Emato International',
          companyName: 'Emato International',
          logo: `https://api.builder.io/api/v1/image/assets/TEMP/d5e646fb3e0bfda819927082f350da22c13d7aa4?width=646`,
          isGlobal: true,
          isVerified: true,
          country: 'United State',
          memberYears: 1,
          businessType: 'Manufacture',
          reviews: 0,
          products: 20
        })));
      } finally {
        setLoading(false);
      }
    };
    fetchSellers();
  }, []);

  // Fetch user's favorite seller IDs
  useEffect(() => {
    const fetchFavoriteIds = async () => {
      if (user?.id) {
        try {
          const response = await favoritesApi.getIds(user.id);
          setFavoriteIds(response.sellerIds || []);
        } catch (error) {
          console.error('Error fetching favorite IDs:', error);
        }
      }
    };
    fetchFavoriteIds();
  }, [user?.id]);

  // Toggle favorite
  const handleToggleFavorite = async (sellerId) => {
    if (!isAuthenticated) {
      alert('Please login to save favorites');
      return;
    }

    try {
      const response = await favoritesApi.toggle(user.id, sellerId);
      if (response.isFavorited) {
        setFavoriteIds(prev => [...prev, sellerId]);
      } else {
        setFavoriteIds(prev => prev.filter(id => id !== sellerId));
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const SellerCard = ({ seller }) => {
    const isFavorited = favoriteIds.includes(seller.id);
    const displayName = seller.name || seller.companyName || 'Unknown Seller';
    const productCount = seller._count?.products || seller.products || 0;
    
    // Handle card click to navigate to seller profile
    const handleCardClick = (e) => {
      // Don't navigate if clicking on favorite button
      if (e.target.closest('button')) return;
      navigate(`/seller/${seller.id}`);
    };
    
    return (
      <div className="top-seller-card" onClick={handleCardClick} style={{ cursor: 'pointer' }}>
        {/* Background Image Container */}
        <div className="top-seller-image-container">
          <img 
            src={seller.coverImage || "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600"} 
            alt="Seller Background" 
            className="top-seller-bg-image"
          />
        </div>
        
        {/* Seller Content Area */}
        <div className="top-seller-content">
          {/* Avatar + Name + Bookmark Container */}
          <div className="top-seller-avatar-row">
            {seller.logo ? (
              <img src={seller.logo} alt={displayName} className="top-seller-avatar" style={{width: 56, height: 56, borderRadius: '50%', objectFit: 'cover'}} />
            ) : (
              <svg className="top-seller-avatar" width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="28" cy="28" r="27.5" fill="#E6EBEF" stroke="#D3D3D3"/>
                <rect x="15" y="18" width="26" height="20" rx="2" fill="#1F396C"/>
                <circle cx="28" cy="28" r="6" fill="white"/>
              </svg>
            )}
            <div className="top-seller-name-wrap">
              <span className="top-seller-name">{displayName}</span>
            </div>
            {/* Bookmark Icon - Next to company name */}
            <button 
              className={`top-seller-bookmark-btn ${isFavorited ? 'favorited' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleToggleFavorite(seller.id);
              }}
              title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 0H11.5C11.1022 0 10.7206 0.158035 10.4393 0.43934C10.158 0.720644 10 1.10218 10 1.5V18C10.0001 18.1338 10.036 18.2652 10.1039 18.3805C10.1719 18.4958 10.2695 18.5908 10.3866 18.6557C10.5037 18.7206 10.636 18.7529 10.7698 18.7494C10.9036 18.7458 11.034 18.7066 11.1475 18.6356L16.75 15.1341L22.3534 18.6356C22.4669 18.7063 22.5972 18.7454 22.7309 18.7488C22.8646 18.7522 22.9967 18.7198 23.1136 18.655C23.2306 18.5902 23.3281 18.4953 23.396 18.3801C23.4639 18.2649 23.4998 18.1337 23.5 18V1.5C23.5 1.10218 23.342 0.720644 23.0607 0.43934C22.7794 0.158035 22.3978 0 22 0ZM22 16.6472L17.1466 13.6144C17.0274 13.5399 16.8896 13.5004 16.7491 13.5004C16.6085 13.5004 16.4708 13.5399 16.3516 13.6144L11.5 16.6472V1.5H22V16.6472Z" 
                  fill={isFavorited ? '#1F396C' : '#969696'}
                />
              </svg>
            </button>
          </div>
          
          {/* Info Section - Badges, Location, Business Type */}
          <div className="top-seller-info-section">
            {/* Badges Row */}
            <div className="top-seller-badges-row">
              {/* Membership Plan Badge */}
              {(() => {
                const plan = seller.membershipPlan || 'Basic';
                const planLower = plan.toLowerCase();
                
                const basicIcon = 'https://api.builder.io/api/v1/image/assets/TEMP/d8ee44da49d1e0c28cf5bfe995ca6aa7f62420ea?width=44';
                const gccIcon = 'https://api.builder.io/api/v1/image/assets/TEMP/cdcdf2f44d98a4cc6cdc74b524bc4359f623ed6c?width=44';
                const globalIcon = 'https://api.builder.io/api/v1/image/assets/TEMP/050559d54da8d35ba6d11841efdb737d4c3ae660?width=44';
                
                let config;
                
                if (planLower.includes('gcc') || planLower.includes('premium') || planLower.includes('gold')) {
                  config = { color: '#009D0A', icon: gccIcon, label: 'GCC Business' };
                } else if (planLower.includes('global') || planLower.includes('enterprise') || planLower.includes('platinum')) {
                  config = { color: '#FF6B00', icon: globalIcon, label: 'Global' };
                } else {
                  config = { color: '#6D6D6D', icon: basicIcon, label: 'Basic' };
                }
                
                return (
                  <div className="top-seller-global-badge">
                    {config.icon && (
                      <img src={config.icon} alt={config.label} className="top-seller-global-icon" />
                    )}
                    <span className="top-seller-global-text" style={{ color: config.color }}>{config.label}</span>
                  </div>
                );
              })()}
            </div>
              {/* Verified/Pending Badge - Based on actual isVerified status */}
              {seller.isVerified ? (
                <svg className="top-seller-verified-badge" width="96" height="30" viewBox="0 0 96 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 15C22 15.9323 20.7329 16.6871 20.5034 17.5472C20.2653 18.4377 20.9782 19.7221 20.528 20.5015C20.0705 21.2923 18.597 21.314 17.9562 21.9562C17.314 22.5984 17.2923 24.0705 16.5014 24.528C15.7221 24.9782 14.4377 24.2653 13.5472 24.5034C12.6871 24.7329 11.9337 26 11 26C10.0663 26 9.31291 24.7329 8.45276 24.5034C7.56231 24.2653 6.27788 24.9782 5.49856 24.528C4.70769 24.0705 4.68605 22.597 4.04382 21.9562C3.4016 21.3154 1.92954 21.2923 1.47205 20.5015C1.02177 19.7221 1.73472 18.4377 1.49659 17.5472C1.26712 16.6871 0 15.9338 0 15C0 14.0663 1.26712 13.3129 1.49659 12.4528C1.73472 11.5623 1.02177 10.2779 1.47205 9.49857C1.92954 8.7077 3.40305 8.68605 4.04382 8.04383C4.68605 7.40161 4.70769 5.92955 5.49856 5.47206C6.27788 5.02178 7.56231 5.73472 8.45276 5.49659C9.31291 5.26712 10.0663 4 11 4C11.9337 4 12.6871 5.26712 13.5472 5.49659C14.4377 5.73472 15.7221 5.02178 16.5014 5.47206C17.2923 5.92955 17.314 7.40305 17.9562 8.04383C18.5984 8.68461 20.0705 8.7077 20.528 9.49857C20.9782 10.2779 20.2653 11.5623 20.5034 12.4528C20.7329 13.3129 22 14.0663 22 15Z" fill="#29ABE2"/>
                  <path d="M16.5876 11.6015C16.1229 11.1368 15.3696 11.1368 14.9049 11.6015L10.0312 16.4751L8.00786 14.4518C7.54315 13.9871 6.7898 13.9871 6.32509 14.4518C5.86039 14.9165 5.86039 15.6698 6.32509 16.1345L9.14798 18.9574C9.39188 19.2013 9.71372 19.3168 10.0327 19.3052C10.3516 19.3168 10.6734 19.2013 10.9173 18.9574L16.5891 13.2857C17.0538 12.821 17.0538 12.0676 16.5891 11.6029L16.5876 11.6015Z" fill="white"/>
                  <text fill="#29ABE2" xmlSpace="preserve" style={{whiteSpace: 'pre'}} fontFamily="Poppins" fontSize="17" letterSpacing="0em"><tspan x="27" y="20.95">Verified</tspan></text>
                </svg>
              ) : (
                <svg className="top-seller-pending-badge" width="96" height="30" viewBox="0 0 96 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="11" cy="15" r="9" stroke="#F5A623" strokeWidth="2" fill="none"/>
                  <path d="M11 10V15.5L14 17" stroke="#F5A623" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <text fill="#F5A623" xmlSpace="preserve" style={{whiteSpace: 'pre'}} fontFamily="Poppins" fontSize="17" letterSpacing="0em"><tspan x="27" y="20.95">Pending</tspan></text>
                </svg>
              )}
            
            {/* Location with Flag */}
            <div className="top-seller-location-wrap">
              <div className="top-seller-location-row">
                <div className="top-seller-flag-container">
                  <img src={seller.countryFlag || "https://flagcdn.com/w80/ae.png"} alt="Flag" className="top-seller-flag-img" />
                </div>
                <span className="top-seller-location-text">
                  <span className="top-seller-country">{seller.country || 'United State'} / </span>
                  <span className="top-seller-years">{seller.memberYears || 1} Year{(seller.memberYears || 1) > 1 ? 's' : ''}</span>
                </span>
              </div>
            </div>
            
            {/* Business Type */}
            <div className="top-seller-business-wrap">
              <div className="top-seller-business-inner">
                <span className="top-seller-business-type">{seller.businessType || 'Manufacturer'}</span>
              </div>
            </div>
          </div>
          
          {/* Stats Row */}
          <div className="top-seller-stats-row">
            <div 
              className="top-seller-stat-item" 
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/seller/${seller.id}?tab=reviews`);
              }}
              style={{ cursor: 'pointer' }}
              title="View seller reviews"
            >
              <span className="top-seller-stat-text">{seller.reviews || 0} Reviews</span>
            </div>
            <div 
              className="top-seller-stat-item"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/supplier/${seller.id}/products`);
              }}
              style={{ cursor: 'pointer' }}
              title="View seller products"
            >
              <span className="top-seller-stat-text">{productCount} Products</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="top-seller-page">
      <Header />
      
      <main className="top-seller-main">
        <div className="top-seller-container">
          {/* Breadcrumb */}
          <div className="top-seller-breadcrumb">
            <span 
              className="breadcrumb-item" 
              onClick={() => navigate('/')}
              style={{ cursor: 'pointer' }}
            >
              Home
            </span>
            <svg className="breadcrumb-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 13L10 9L6 5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="breadcrumb-item active">Top seller</span>
          </div>
          
          {/* Page Header */}
          <div className="top-seller-header">
            <div className="top-seller-title-section">
              <h1 className="top-seller-title">Top Supplier</h1>
              <p className="top-seller-subtitle">Discover the most trusted suppliers chosen by thousands of buyers.</p>
            </div>
          </div>
          
          {/* Sellers Grid */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              Loading suppliers...
            </div>
          ) : sellers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              No suppliers found
            </div>
          ) : (
            <div className="top-seller-grid">
              {sellers.map((seller) => (
                <SellerCard key={seller.id} seller={seller} />
              ))}
            </div>
          )}
          
          {/* Pagination */}
          <Pagination currentPage={currentPage} totalPages={5} onPageChange={setCurrentPage} />
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default TopSellerPage;
